import React, { useMemo } from "react";
import {
  Box,
  Heading,
  HStack,
  Text,
  Badge,
  Spinner,
  Button,
  Divider,
  SimpleGrid,
} from "@chakra-ui/react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAdmission, useDischargeAdmission } from "../apis/patients";
import AdmissionNotesPanel from "../features/admissions/AdmissionNotesPanel";
import AdmissionDocumentationEditor from "../features/admissions/AdmissionDocumentationEditor";
import AdmissionReviewsPanel from "../features/admissions/AdmissionReviewsPanel";
import AdmissionTreatmentPanel from "../features/admissions/AdmissionTreatmentPanel";
import AdmissionTreatmentAdminLog from "../features/admissions/AdmissionTreatmentAdminLog";

const fmt = (iso?: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

export default function AdmissionDetailPage() {
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id: string }>();
  const id = routeId || "";

  const location = useLocation();
  const locationState = (location.state || {}) as {
    initialSeed?: string;
  };
  const initialSeed = locationState.initialSeed;

  const { data: rawAd, isLoading } = useAdmission(id);

  const ad = useMemo(() => {
    if (!rawAd) return undefined;
    if ((rawAd as any).patientId) return rawAd as any;
    if ((rawAd as any).data?.patientId) return (rawAd as any).data;
    return rawAd as any;
  }, [rawAd]);

  const { mutate: discharge, isPending } = useDischargeAdmission(ad?.patientId);

  if (!id) return <Text>Invalid admission id.</Text>;
  if (isLoading) return <Spinner />;
  if (!ad) return <Text>Admission not found.</Text>;

  const admissionIdForEditor: string = ad._id || id;
  const canDischarge = ad.status === "admitted";

  const appointmentId = (ad as any).appointmentId || undefined;

  return (
    <Box p={4}>
      {/* Header */}
      <HStack justify="space-between" mb={3}>
        <Heading size="md">Admission Detail</Heading>
        <Button onClick={() => navigate(-1)} variant="ghost" size="sm">
          Back
        </Button>
      </HStack>

      {/* SECTION 1: Admission summary */}
      <Box p={4} borderWidth="1px" borderRadius="md" bg="white" mb={4}>
        <HStack mb={2} spacing={3} flexWrap="wrap">
          <Badge
            colorScheme={
              ad.status === "admitted"
                ? "green"
                : ad.status === "discharged"
                ? "gray"
                : "yellow"
            }
          >
            {ad.status}
          </Badge>
          <Text>
            Patient:{" "}
            <Link
              to={`/patients/${ad.patientId}`}
              style={{ textDecoration: "underline" }}
            >
              {ad.patientName || ad.patientId}
            </Link>
          </Text>
        </HStack>

        <HStack spacing={6} flexWrap="wrap">
          <Text>
            <b>Admitted At:</b> {fmt(ad.admittedAt ?? ad.createdAt)}
          </Text>
          <Text>
            <b>Discharged At:</b> {fmt(ad.dischargedAt)}
          </Text>
          <Text>
            <b>Ward/Bed:</b>{" "}
            {[ad.ward, ad.bed].filter(Boolean).join(" / ") || "—"}
          </Text>
        </HStack>

        {canDischarge && (
          <Button
            mt={3}
            size="sm"
            colorScheme="red"
            isLoading={isPending}
            onClick={() => discharge({ admissionId: ad._id })}
          >
            Discharge
          </Button>
        )}
      </Box>

      {/* SECTION 2: Preliminary documentation */}
      <Heading size="sm" mb={2}>
        Preliminary Admission Documentation
      </Heading>
      <AdmissionDocumentationEditor
        admissionId={admissionIdForEditor}
        initialSeed={initialSeed}
      />

      <Divider my={6} />

      {/* SECTION 3: Doctor reviews + Nurses / staff notes */}
      <Heading size="sm" mb={3}>
        Clinical Timeline
      </Heading>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
        <Box>
          <Heading size="xs" mb={2}>
            Doctor Reviews / Ward Rounds
          </Heading>
          <AdmissionReviewsPanel admissionId={ad._id} />
        </Box>

        <Box>
          <Heading size="xs" mb={2}>
            Nurses & Other Staff Notes
          </Heading>
          <AdmissionNotesPanel admissionId={ad._id} />
        </Box>
      </SimpleGrid>

      {/* SECTION 4: Treatment plan */}
      <Heading size="sm" mb={2}>
        Treatment Plan (from admitting appointment)
      </Heading>
      <AdmissionTreatmentPanel
        admissionId={ad._id}
        appointmentId={ad.appointmentId}
      />

      <Divider my={6} />

      <Heading size="sm" mb={2}>
        Treatment Administration Log
      </Heading>
      <AdmissionTreatmentAdminLog
        admissionId={ad._id}
        appointmentId={ad.appointmentId}
      />
    </Box>
  );
}
